import { DriveItemPresentationPolicy } from './drive-item-presentation.policy';
import { DriveItem, ItemType } from './drive-viewer.models';
import { DriveMoveState, DrivePreviewState, DriveWorkspaceState } from './drive-viewer.state';

export class DriveSelectionQuery {
  constructor(
    private readonly workspace: DriveWorkspaceState,
    private readonly move: DriveMoveState,
    private readonly preview: DrivePreviewState,
    private readonly presentation: DriveItemPresentationPolicy,
    private readonly translate: (key: string) => string
  ) {}

  selectedItem(): DriveItem | undefined {
    const selectedId = Array.from(this.workspace.selectedIds)[0];
    return selectedId ? this.workspace.items.find((item) => item.id === selectedId) : undefined;
  }

  rootFolder(): DriveItem | undefined {
    return this.workspace.items.find((item) => item.type === 'folder' && item.parentId == null);
  }

  currentPath(): string[] {
    return this.workspace.items.find((item) => item.id === this.workspace.currentFolderId)?.path || ['Mi unidad'];
  }

  visibleItems(): DriveItem[] {
    const currentId = String(this.workspace.currentFolderId);
    const hasQuery = !!this.workspace.search.trim();
    const base = hasQuery
      ? this.workspace.items.filter((item) => !this.isUnderByDateItem(item))
      : this.workspace.items.filter((item) => String(item.parentId ?? '') === currentId);
    const matching = base
      .filter((item) => this.matchesTypeAndOwner(item))
      .filter((item) => !hasQuery || this.matchesSearch(item));
    return this.sortFoldersFirst(matching);
  }

  currentPreviewItem(): DriveItem | undefined {
    return this.preview.previewItemOverride ?? this.visibleItems()[this.preview.previewIndex];
  }

  folderOptions(): DriveItem[] {
    return this.workspace.items
      .filter((item) => item.type === 'folder' && item.parentId !== undefined && !this.isVirtualFolderId(item.id))
      .sort((first, second) => this.folderLabel(first).localeCompare(
        this.folderLabel(second), undefined, { sensitivity: 'base' }
      ));
  }

  moveDialogFolderOptions(): DriveItem[] {
    const query = this.move.moveDialogSearch.trim().toLowerCase();
    return this.folderOptions().filter((folder) => {
      const valid = this.move.moveDialogFiles.some((file) => this.canMoveFileToFolder(file, folder.id));
      return valid && (!query || `${folder.name} ${folder.path.join(' / ')}`.toLowerCase().includes(query));
    });
  }

  folderDropListIds(): string[] {
    return this.folderOptions().flatMap((folder) => [
      this.dropListId('tree', folder),
      this.dropListId('list', folder),
      this.dropListId('grid', folder),
      this.dropListId('mobile', folder)
    ]);
  }

  childTreeItems(parent: string | null): DriveItem[] {
    const parentId = parent == null ? null : String(parent);
    return this.workspace.items
      .filter((item) => (item.parentId == null ? null : String(item.parentId)) === parentId)
      .sort((first, second) => {
        if (first.type === 'folder' && second.type !== 'folder') return -1;
        if (first.type !== 'folder' && second.type === 'folder') return 1;
        return first.name.localeCompare(second.name, undefined, { sensitivity: 'base' });
      });
  }

  childFolders(parent: string | null): DriveItem[] {
    return this.childTreeItems(parent).filter((item) => item.type === 'folder');
  }

  hasChildren(parent: string): boolean {
    return this.workspace.items.some((item) => String(item.parentId ?? '') === String(parent ?? ''));
  }

  isUnderByDateItem(item: DriveItem): boolean {
    return this.isVirtualFolderId(item.id) || this.isVirtualFolderId(item.parentId == null ? null : String(item.parentId));
  }

  isVirtualFolderId(id: string | null | undefined): boolean {
    return !!id && (id === 'byDate' || id.startsWith('g_'));
  }

  dropListId(scope: 'tree' | 'list' | 'grid' | 'mobile', folder: DriveItem): string {
    return `${scope}Drop_${String(folder.id).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  }

  canMoveFileToFolder(file: DriveItem, folderId: string): boolean {
    const contentId = Number.parseInt(file.id, 10);
    return String(file.parentId ?? '') !== folderId && Number.isFinite(contentId) && !!file.documentId;
  }

  selectedFiles(): DriveItem[] {
    return Array.from(this.workspace.selectedIds)
      .map((id) => this.workspace.items.find((item) => item.id === id))
      .filter((item): item is DriveItem => !!item && item.type !== 'folder');
  }

  isSelected(itemId: string): boolean { return this.workspace.selectedIds.has(itemId); }

  fileTypeLabel(item: DriveItem): string {
    if (item.type === 'folder') return this.translate('drive.type.folder');
    const extension = this.presentation.extension(item);
    if (extension && ['eml', 'msg', 'email'].includes(extension)) {
      return this.translate('drive.type.email') || 'Correo';
    }
    if (extension) return extension.toUpperCase();
    const keyByType: Partial<Record<ItemType, string>> = {
      doc: 'drive.type.doc', sheet: 'drive.type.sheet', slide: 'drive.type.slide',
      image: 'drive.type.image', video: 'drive.type.video', zip: 'drive.type.zip',
      pdf: 'drive.type.pdf', other: 'drive.type.other'
    };
    return item.type === 'txt' ? 'TXT' : this.translate(keyByType[item.type] || 'drive.type.other');
  }

  private matchesTypeAndOwner(item: DriveItem): boolean {
    const typeMatches = this.workspace.filterType === 'all' || item.type === this.workspace.filterType;
    const ownerMatches = this.workspace.filterOwner === 'all'
      || this.workspace.filterOwner === 'any'
      || item.owner === this.workspace.filterOwner;
    return typeMatches && ownerMatches;
  }

  private matchesSearch(item: DriveItem): boolean {
    const normalize = (value: string) => value.toLowerCase().normalize('NFD')
      .replace(/\p{Diacritic}/gu, '').trim();
    const query = normalize(this.workspace.search);
    const terms = query.split(/\s+/).filter(Boolean);
    const content = normalize(`${item.name} ${item.path?.join(' ') || ''}`);
    return content.includes(query) || terms.every((term) => content.includes(term));
  }

  private sortFoldersFirst(items: DriveItem[]): DriveItem[] {
    const direction = this.workspace.sortDir === 'asc' ? 1 : -1;
    const byName = (first: DriveItem, second: DriveItem) => first.name.localeCompare(
      second.name, undefined, { sensitivity: 'base' }
    ) * direction;
    const compare = this.comparator(direction, byName);
    return [
      ...items.filter((item) => item.type === 'folder').sort(compare),
      ...items.filter((item) => item.type !== 'folder').sort(compare)
    ];
  }

  private comparator(direction: number, byName: (first: DriveItem, second: DriveItem) => number) {
    return (first: DriveItem, second: DriveItem): number => {
      let result = 0;
      if (this.workspace.sortKey === 'modified') result = Date.parse(first.modified) - Date.parse(second.modified);
      if (this.workspace.sortKey === 'size' && first.type !== 'folder') result = first.size - second.size;
      if (this.workspace.sortKey === 'type') result = this.fileTypeLabel(first).localeCompare(this.fileTypeLabel(second));
      if (this.workspace.sortKey === 'owner') result = this.ownerRank(first) - this.ownerRank(second);
      return result === 0 ? byName(first, second) : result * direction;
    };
  }

  private ownerRank(item: DriveItem): number { return item.owner === 'you' ? 0 : item.owner === 'external' ? 1 : 2; }
  private folderLabel(item: DriveItem): string { return item.path?.join('/') || item.name; }
}
